/**
 * Notification Service — Fetches from the real notifications table via CRUD.
 *
 * Reads from GET /api/notifications?user_profile_id=<id>&sort=created_at&order=desc
 * and provides mark-as-read and delete operations.
 */

import { httpClient } from '../api/http-client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  linkTo: string;
  createdAt: string;
  read: boolean;
  data?: Record<string, unknown>;
}

interface RawNotification {
  id: string;
  user_profile_id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  read: boolean;
  created_at: string;
}

// ─── Link mapping by notification type ────────────────────────────────────────

function getLinkForType(type: string, data?: Record<string, unknown> | null): string {
  switch (type) {
    case 'buddy_group_invite':
      return '/my/buddy-groups';
    case 'buddy_group_invite_accepted':
    case 'buddy_group_invite_declined':
      return data?.buddy_group_id
        ? `/my/buddy-groups/${data.buddy_group_id}`
        : '/my/buddy-groups';
    case 'buddy_group_member_removed':
    case 'buddy_group_invitation_cancelled':
    case 'buddy_group_deleted':
    case 'buddy_group_member_left':
      return '/my/buddy-groups';
    case 'buddy_group_registered':
      // Deep-link to the rodeo page where the group was registered.
      // Falls back to a generic page if the data shape is unexpected.
      if (typeof data?.url === 'string') return data.url as string;
      return data?.rodeo_id
        ? `/rodeos/${data.rodeo_id}`
        : '/my/buddy-groups';
    case 'partner_invitation':
      return data?.event_id
        ? `/events/${data.event_id}`
        : '/dashboard';
    case 'partner_accepted':
    case 'partner_declined':
      return data?.event_id
        ? `/events/${data.event_id}`
        : '/dashboard';
    case 'on_behalf_registration':
    case 'on_behalf_cancellation':
      // Buddy-group primary registered/cancelled the recipient on a
      // specific event — deep-link to that event's detail page so the
      // member can review.
      return data?.event_id
        ? `/events/${data.event_id}`
        : '/dashboard';
    case 'event_entry_confirmation':
      // Recipient successfully entered an event — surface the event
      // page so they can review performance details, change payment
      // method, or manage the entry.
      return data?.event_id
        ? `/events/${data.event_id}`
        : '/dashboard';
    case 'membership_claim_pending':
      // Admin / delegate notification: deep-link straight into the
      // Manage Users page with the Flagged filter on so they land on
      // the pending claim ready to approve or reject.
      return '/users?flagged=1';
    case 'membership_claim_approved':
    case 'membership_claim_rejected':
      // Recipient (the contestant) — surface the funds-due page so they
      // can see any non-member surcharge that may have been added.
      return '/profile?tab=payments';
    case 'manual_ledger_charge':
    case 'manual_ledger_credit':
      // Admin recorded a charge/credit on the contestant's org ledger —
      // deep-link to their Financials tab (Organization ledger + the full
      // transaction history) so they can see it in context.
      return '/profile?tab=financials';
    case 'ach_verification_needed':
      // Bank (ACH) verification needed — deep-link straight to Stripe's
      // hosted verification page so the user can finish verifying their bank.
      // This is an EXTERNAL (https) URL; the click handler opens absolute URLs
      // in a new tab rather than routing in-app. Falls back to the funds tab
      // (where the entry shows as unpaid with Verify / Pay actions) when the
      // URL is missing or has expired past the verification window.
      return typeof data?.verification_url === 'string' &&
        data.verification_url.length > 0
        ? (data.verification_url as string)
        : '/profile?tab=payments';
    default:
      return '/notifications';
  }
}

/**
 * A resolved notification `linkTo` is external when it's an absolute http(s)
 * URL (e.g. Stripe's hosted ACH verification page). Those must be opened in a
 * new tab — react-router `navigate()` only handles in-app routes.
 */
export function isExternalNotificationLink(linkTo: string): boolean {
  return /^https?:\/\//i.test(linkTo);
}

// ─── Service Functions ────────────────────────────────────────────────────────

/** Shape of the paginated CRUD list response wrapper. */
interface NotificationListResponse {
  data: RawNotification[];
  total?: number;
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
}

// Page size used when walking the paginated notifications endpoint. The
// backend default cap is 1000; a smaller page keeps each response light
// while the loop below still retrieves the complete history.
const PAGE_SIZE = 200;
// Hard ceiling on pages walked, purely as an infinite-loop guard in case
// the backend ever returns a malformed `hasMore`. 500 * 200 = 100k rows.
const MAX_PAGES = 500;

function mapNotification(n: RawNotification): AppNotification {
  return {
    id: n.id,
    type: n.type,
    title: n.title,
    body: n.message,
    linkTo: getLinkForType(n.type, n.data),
    createdAt: n.created_at,
    read: n.read ?? false,
    data: n.data ?? undefined,
  };
}

/**
 * Fetch ALL notifications for the current user.
 *
 * Pages through the notifications endpoint (newest-first) until the
 * server reports no more rows, so the full history is returned rather
 * than just the first page. Ordering is stable across pages because the
 * server sorts by created_at desc.
 */
export async function fetchNotifications(userProfileId: string): Promise<AppNotification[]> {
  const all: AppNotification[] = [];

  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await httpClient.get<NotificationListResponse>(
      `/notifications?user_profile_id=${userProfileId}` +
        `&sort=created_at&order=desc&limit=${PAGE_SIZE}&page=${page}`
    );

    const rows = res?.data ?? [];
    all.push(...rows.map(mapNotification));

    // Stop when the server says there is nothing more, or when a short
    // page (fewer rows than requested) signals the last page even if
    // `hasMore` is absent from an older response shape.
    if (!res?.hasMore || rows.length < PAGE_SIZE) break;
  }

  return all;
}

/**
 * Fetch only unread notifications for badge count.
 */
export async function fetchUnreadNotifications(userProfileId: string): Promise<AppNotification[]> {
  const all = await fetchNotifications(userProfileId);
  return all.filter((n) => !n.read);
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(notificationId: string): Promise<void> {
  await httpClient.put(`/notifications/${notificationId}`, { read: true });
}

/**
 * Mark all notifications as read for a user.
 */
export async function markAllNotificationsRead(userProfileId: string): Promise<void> {
  // Fetch unread, then mark each one — no bulk endpoint exists
  const unread = await fetchUnreadNotifications(userProfileId);
  await Promise.all(unread.map((n) => markNotificationRead(n.id)));
}

/**
 * Delete a single notification.
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  await httpClient.delete(`/notifications/${notificationId}`);
}
